import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import styles from "./styles.module.css";
import { useState, useEffect, useCallback } from "react";
import SnackBar from "../../SnackBar";
import SnackBarProgresso from "../../SnackBarProgresso";
import useClientPolling from "../../../hooks/useClientPolling";
import { buscaCategoriasTribPorTipoNegocio } from "../../../services/api-planejamento-tributario.service";

const ModalNovaVersao = ({
  open,
  onClose,
  onCreate,
  loading = false,
  tipoPrevia = "PIS/COFINS", // Tipo de prévia para buscar categorias
  idPrevia = null, // ID da prévia
  dadosPrevia = null, // Dados da prévia para obter razão social
  versoesExistentes = [], // Lista de versões existentes para calcular próximo número
  isProjeto = false, // Se é projeto ou prévia
}) => {
  const [nomeVersao, setNomeVersao] = useState("");
  const [categoria, setCategoria] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  // Hook Client Polling para gerenciar progresso em tempo real
  const {
    progresso,
    erro,
    resultado,
    loading: sseLoading,
    criarVersaoComMotor,
    limparEstado,
  } = useClientPolling();

  // ✅ NOVA FUNCIONALIDADE: Fechar modal automaticamente durante progresso
  useEffect(() => {
    // Se há progresso ativo, fechar o modal de nova versão
    if (progresso || sseLoading) {
      onClose();
    }
  }, [progresso, sseLoading, onClose]);

  // Função para mapear etapas para números
  const mapearEtapa = useCallback((etapa) => {
    const etapas = {
      inicio: 1,
      validacao: 2,
      criacao: 3,
      motores: 4,
      motor_formula_cofins: 5,
      motor_formula_pis: 6,
      concluido: 7,
    };
    return etapas[etapa] || 1;
  }, []);

  // Função para gerar nome automático da versão
  const gerarNomeVersao = useCallback(() => {
    if (!dadosPrevia) return "";

    // Obter primeiro nome da razao social
    const razaoSocial = dadosPrevia.razaoSocial || "Empresa";
    const primeiroNome = razaoSocial.split(" ")[0];

    // Nome da categoria selecionada
    const categoriaLabel = categoria?.label || categoria?.descricao || "";

    // Obter data atual no formato DD/MM/YYYY
    const dataAtual = new Date().toLocaleDateString("pt-BR");

    // Determinar tipo (Previa ou Projeto)
    const tipo = isProjeto ? "Projeto" : "Previa";

    // Calcular proximo numero da versao
    // Filtrar versoes que nao sao "Original" (idPreviaVersao !== 0)
    const versoesValidas = versoesExistentes.filter(
      (versao) => versao.idPreviaVersao !== 0 && versao.idPreviaVersao !== null
    );
    const proximoNumero = versoesValidas.length + 1;

    // Gerar nome: Tipo + Primeiro nome + Categoria + Data + Numero da versao
    // Ex.: "Previa ACME CategoriaX 01/01/2025 3"
    return `${tipo} ${primeiroNome} ${categoriaLabel} ${dataAtual} ${proximoNumero}`;
  }, [dadosPrevia, categoria, isProjeto, versoesExistentes]);

  // Atualizar nome da versao quando dados da previa mudarem ou categoria for selecionada
  useEffect(() => {
    if (open && dadosPrevia && categoria) {
      const nomeGerado = gerarNomeVersao();
      setNomeVersao(nomeGerado);
    } else if (open && !categoria) {
      setNomeVersao("");
    }
  }, [open, dadosPrevia, categoria, gerarNomeVersao]);

  // Buscar categorias por tipo de negócio
  useEffect(() => {
    const fetchCategorias = async () => {
      if (!open) return;

      setLoadingCategorias(true);
      try {
        const response = await buscaCategoriasTribPorTipoNegocio(tipoPrevia);

        // Mapear resposta da API para formato do Autocomplete
        const categoriasFormatadas =
          response.categorias?.map((cat) => ({
            id: cat.idCategoriaTributaria, // Para compatibilidade com Autocomplete
            idCategoriaTributaria: cat.idCategoriaTributaria, // ID original da API
            label: cat.descricao,
            descricao: cat.descricao,
            ...cat, // Preservar todos os dados originais
          })) || [];

        setCategorias(categoriasFormatadas);
      } catch (error) {
        console.error("Erro ao buscar categorias:", error);
        setObjSnackBar({
          type: "error",
          msg: "Erro ao carregar categorias.",
        });
      } finally {
        setLoadingCategorias(false);
      }
    };

    fetchCategorias();
  }, [open, tipoPrevia]);

  const handleSubmit = () => {
    if (!categoria) {
      setObjSnackBar({
        type: "error",
        msg: "Categoria e obrigatoria.",
      });
      return;
    }

    if (!nomeVersao.trim()) {
      setObjSnackBar({
        type: "error",
        msg: "Nome da versao e obrigatorio.",
      });
      return;
    }

    // Executar criação usando Client Polling
    handleCreateWithPolling();
  };

  const handleCreateWithPolling = async () => {
    try {
      // Limpar estado anterior
      limparEstado();

      // Preparar payload para Client Polling
      const payload = {
        tipoPrevia,
        descricao: nomeVersao,
        idPrevia,
        // idGrupoEconomico removido - será buscado automaticamente pelo CNPJ da prévia
        idCategoria: categoria.idCategoriaTributaria,
        usuCadastro: parseInt(localStorage.getItem("userId") || "1"),
      };

      // Usar Client Polling para criar versão
      await criarVersaoComMotor(payload);
    } catch (error) {
      console.error("Erro ao criar versão:", error);
      setObjSnackBar({
        type: "error",
        msg: "Erro ao criar versao",
      });
    }
  };

  // Efeito para tratar resultado do Client Polling
  useEffect(() => {
    if (resultado) {
      if (resultado.sucesso) {
        // Sucesso - fechar modal e chamar callback
        setObjSnackBar({
          type: "success",
          msg: resultado.mensagem || "Versao criada com sucesso!",
        });

        // Limpar campos
        setNomeVersao("");
        setCategoria(null);

        // Chamar callback de sucesso
        if (onCreate) {
          onCreate(resultado);
        }

        // Fechar modal apos um pequeno delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Erro
        setObjSnackBar({
          type: "error",
          msg: resultado.mensagem || "Erro ao criar versão",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultado]); // Apenas 'resultado' nas dependências (onCreate/onClose são callbacks estáveis)

  // Erro do SSE é tratado pelo SnackBarProgresso, não duplicar aqui

  // Removido: handleCloseConfirmModal (não necessário para criação)

  const handleClose = () => {
    setNomeVersao("");
    setCategoria(null);
    setObjSnackBar({ type: "", msg: "" });
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <div className={styles.title}>
            <CopyAllIcon sx={{ marginRight: ".5rem" }} />
            Nova Versão
          </div>
        </DialogTitle>
        <DialogContent className={styles.content}>
          <Autocomplete
            value={categoria}
            onChange={(_, value) => {
              setCategoria(value);
            }}
            options={categorias}
            getOptionLabel={(option) => option.label || ""}
            fullWidth
            loading={loadingCategorias}
            disabled={loadingCategorias}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                placeholder={
                  loadingCategorias
                    ? "Carregando categorias..."
                    : "Selecione uma categoria"
                }
              />
            )}
          />
          <TextField
            label="Nome da Versão"
            variant="outlined"
            fullWidth
            value={nomeVersao}
            InputProps={{
              readOnly: true,
            }}
            placeholder="Selecione uma categoria para gerar o nome"
          />
          <DialogActions className={styles.containerBtn}>
            <Button
              onClick={handleClose}
              variant="contained"
              className={styles.btnCancelar}
              disabled={sseLoading}
            >
              Fechar
            </Button>
            <Button
              variant="contained"
              className={styles.btnSalvar}
              onClick={handleSubmit}
              disabled={!categoria || sseLoading}
              startIcon={sseLoading ? <CircularProgress size={16} /> : null}
            >
              {sseLoading ? "Criando..." : "Criar"}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* SnackBar de Progresso SSE */}
      <SnackBarProgresso
        open={sseLoading || progresso || erro || resultado}
        onClose={() => {
          limparEstado();
          // ✅ NOVA FUNCIONALIDADE: Reabrir modal de versão após sucesso
          if (resultado && resultado.sucesso) {
            // Chamar função para reabrir modal de versão (será implementada no ModalVersoes)
            setTimeout(() => {
              window.reabrirModalVersoes?.();
            }, 1000);
          }
        }}
        progressData={(() => {
          // ✅ CORREÇÃO COMPLETA: Mapeamento de dados SSE
          const mappedData = {
            titulo: progresso?.mensagem || "Executando motores...",
            progresso: progresso?.progresso || 0,
            etapaAtual: mapearEtapa(progresso?.etapa) || 1, // ✅ Mapear etapa para número
            totalEtapas: 7, // ✅ Total de etapas conhecidas
            descricao: progresso?.descricao || progresso?.mensagem,
            tipoMotor: tipoPrevia,
            tempoEstimado: progresso?.tempoEstimado || "Calculando...",
            sucesso: erro ? false : resultado ? true : undefined,
            erro: erro ? true : false,
            etapaOriginal: progresso?.etapa,
            detalhes: progresso?.detalhes,
          };

          return mappedData;
        })()}
        showLoading={sseLoading && !erro && !resultado}
      />

      {/* SnackBar tradicional */}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalNovaVersao;
